import { useCallback, useEffect, useState } from "react";

import api, { formatApiError } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import AcknowledgmentRevokeModal from "./AcknowledgmentRevokeModal";
import SendReminderButton from "./SendReminderButton";

const OFFICE = ["owner", "ops"];

/**
 * Phase 5c — internal sidebar panel on DiagnosticReportPage.
 *
 * Shows all non-revoked acks against the report. Owner/ops also see
 * sensitive fields (email, IP, user agent, revoke button); techs see
 * a slim view (server-enforced via slim model).
 *
 * Stale acks (taken against an older finalized version, then the report
 * was reopened + re-finalized) are clearly labeled.
 */
export default function OwnerAcknowledgmentPanel({ report }) {
  const { user } = useAuth();
  const isOffice = OFFICE.includes(user?.role);

  const [acks, setAcks] = useState([]);
  const [err, setErr] = useState("");
  const [revokeFor, setRevokeFor] = useState(null);

  const reportId = report?.id;
  const reportFinalized = report?.status === "finalized";
  const hasActiveAck = acks.some((a) => !a.revoked_at && !a.is_stale);

  const load = useCallback(async () => {
    if (!reportId) return;
    setErr("");
    try {
      const r = await api.get(
        `/diagnostic-reports/${reportId}/acknowledgments`
      );
      setAcks(r.data || []);
    } catch (e) {
      setErr(formatApiError(e));
    }
  }, [reportId]);

  useEffect(() => {
    load();
  }, [load]);

  if (!reportId) return null;

  return (
    <section
      data-testid="ack-panel"
      className="bg-white border border-slate-200 rounded-md p-4"
    >
      <div className="flex items-center justify-between gap-2">
        <div className="font-heading text-sm font-semibold text-slate-900">
          Owner acknowledgment
        </div>
        <span
          data-testid="ack-count"
          className="text-[10px] uppercase tracking-wider text-slate-500 font-bold"
        >
          {acks.length} {acks.length === 1 ? "entry" : "entries"}
        </span>
      </div>
      <div className="text-[11px] text-slate-500 mt-0.5 mb-3">
        Tokenized public acks · immutable · soft-revoke only
      </div>

      {isOffice && reportFinalized && (
        <div className="mb-4">
          <SendReminderButton
            reportId={reportId}
            hasActiveAck={hasActiveAck}
            onSent={load}
          />
        </div>
      )}

      {err && (
        <div
          data-testid="ack-panel-error"
          className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800 mb-3"
        >
          {err}
        </div>
      )}

      {acks.length === 0 ? (
        <div
          data-testid="ack-empty"
          className="rounded-md bg-slate-50 border border-dashed border-slate-200 px-3 py-3 text-xs text-slate-500"
        >
          No acknowledgment recorded yet.
        </div>
      ) : (
        <ul className="space-y-3">
          {acks.map((a) => (
            <li
              key={a.id}
              data-testid={`ack-row-${a.id}`}
              className={`rounded-md border px-3 py-3 text-xs ${
                a.is_stale
                  ? "border-amber-300 bg-amber-50/70"
                  : "border-emerald-200 bg-emerald-50/40"
              }`}
            >
              <div className="flex flex-wrap items-center gap-2">
                <span
                  className={`inline-flex items-center rounded-md border px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide ${
                    a.is_stale
                      ? "bg-amber-100 text-amber-900 border-amber-300"
                      : "bg-emerald-100 text-emerald-900 border-emerald-300"
                  }`}
                >
                  {a.is_stale ? "Stale" : "Acknowledged"}
                </span>
                {a.share_scope_at_ack && (
                  <span className="inline-flex items-center rounded-md border border-slate-300 bg-white px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-slate-700">
                    {a.share_scope_at_ack}
                  </span>
                )}
                <span className="text-slate-600">
                  {new Date(a.created_at).toLocaleString()}
                </span>
              </div>

              <div className="mt-1.5 text-slate-800">
                <span className="font-semibold">
                  {a.acknowledger_name || "Anonymous"}
                </span>
                {isOffice && a.acknowledger_email && (
                  <span className="text-slate-500">
                    {" · "}
                    {a.acknowledger_email}
                  </span>
                )}
              </div>

              {a.comment && (
                <div className="mt-1.5 whitespace-pre-wrap text-slate-700">
                  &ldquo;{a.comment}&rdquo;
                </div>
              )}

              {a.is_stale && (
                <div className="mt-1.5 text-[11px] text-amber-900">
                  Acknowledged on a prior finalized version of this report.
                </div>
              )}

              {isOffice && (
                <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] text-slate-500">
                  {a.ip_address && (
                    <span data-testid={`ack-ip-${a.id}`}>
                      IP: <code className="font-data">{a.ip_address}</code>
                    </span>
                  )}
                  {a.user_agent && (
                    <span
                      data-testid={`ack-ua-${a.id}`}
                      className="truncate max-w-[260px]"
                      title={a.user_agent}
                    >
                      UA: {a.user_agent}
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={() => setRevokeFor(a.id)}
                    data-testid={`ack-revoke-btn-${a.id}`}
                    className="ml-auto text-[11px] font-semibold text-red-700 hover:text-red-900"
                  >
                    Revoke
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      <AcknowledgmentRevokeModal
        open={!!revokeFor}
        onClose={() => setRevokeFor(null)}
        reportId={reportId}
        ackId={revokeFor}
        onRevoked={() => {
          setRevokeFor(null);
          load();
        }}
      />
    </section>
  );
}
